import type { AnalyticsFilter, Installation, SeedEventRow, Subscription } from "@seed-factory/core";
import type { MetricSnapshotRow } from "./local-types";

export type FunnelInput = {
  installations: Installation[];
  subscriptions: Subscription[];
  events: SeedEventRow[];
  metricSnapshots: MetricSnapshotRow[];
};

export type FunnelStage = {
  stage:
    | "impressions"
    | "listing_views"
    | "installs"
    | "activations"
    | "onboarding"
    | "first_value"
    | "trial"
    | "paid";
  value: number;
  stepConversionRate: number | null;
};

function inWindow(value: string | null, filter: AnalyticsFilter): boolean {
  if (!value) return false;
  const date = new Date(value);
  return date >= filter.from && date <= filter.to;
}

function overlapsWindow(start: string, end: string, filter: AnalyticsFilter): boolean {
  return new Date(start) <= filter.to && new Date(end) >= filter.from;
}

function externalOrEventTotal(
  metricName: string,
  eventFamily: string,
  input: FunnelInput,
  filter: AnalyticsFilter,
): number {
  const snapshots = input.metricSnapshots.filter(
    (row) =>
      row.metricName === metricName && overlapsWindow(row.periodStart, row.periodEnd, filter),
  );
  if (snapshots.length > 0) return snapshots.reduce((sum, row) => sum + row.value, 0);
  return input.events
    .filter((row) => row.eventFamily === eventFamily && inWindow(row.occurredAt, filter))
    .reduce((sum, row) => sum + (row.value ?? 1), 0);
}

export function buildFunnel(input: FunnelInput, filter: AnalyticsFilter): FunnelStage[] {
  const raw = [
    ["impressions", externalOrEventTotal("impressions", "distribution", input, filter)],
    ["listing_views", externalOrEventTotal("listing_views", "listing", input, filter)],
    ["installs", input.installations.filter((row) => inWindow(row.installedAt, filter)).length],
    ["activations", input.installations.filter((row) => inWindow(row.activatedAt, filter)).length],
    [
      "onboarding",
      input.installations.filter((row) => inWindow(row.onboardingCompletedAt, filter)).length,
    ],
    ["first_value", input.installations.filter((row) => inWindow(row.firstValueAt, filter)).length],
    ["trial", input.installations.filter((row) => inWindow(row.trialStartedAt, filter)).length],
    ["paid", input.subscriptions.filter((row) => inWindow(row.startedAt, filter)).length],
  ] as const;

  return raw.map(([stage, value], index) => {
    const previous = index === 0 ? null : (raw[index - 1]?.[1] ?? 0);
    return {
      stage,
      value,
      stepConversionRate: previous === null || previous === 0 ? null : value / previous,
    };
  });
}
